import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  AuthSession,
  AuthState,
  AuthStatus,
  AuthTenant,
  AuthUser,
} from "@/types";
import {
  getStoredAccessToken,
  getStoredRefreshToken,
} from "@/utils/auth-storage";

const storedAccessToken = getStoredAccessToken();
const storedRefreshToken = getStoredRefreshToken();

const initialState: AuthState = {
  user: null,
  tenant: null,
  accessToken: storedAccessToken,
  refreshToken: storedRefreshToken,
  status: storedAccessToken ? "idle" : "anonymous",
  isAuthenticated: Boolean(storedAccessToken),
  showAccountLocked: false,
  sessionExpiredNoticeId: 0,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setAuthStatus: (state, action: PayloadAction<AuthStatus>) => {
      state.status = action.payload;
      state.isAuthenticated = action.payload === "authenticated";
    },
    setAuthSession: (state, action: PayloadAction<AuthSession>) => {
      state.user = action.payload.user;
      state.tenant = action.payload.tenant;
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken ?? null;
      state.status = "authenticated";
      state.isAuthenticated = true;
    },
    setAccessToken: (state, action: PayloadAction<string | null>) => {
      state.accessToken = action.payload;
      state.isAuthenticated = Boolean(action.payload);
      state.status = action.payload ? "authenticated" : "anonymous";
    },
    setRefreshToken: (state, action: PayloadAction<string | null>) => {
      state.refreshToken = action.payload;
    },
    setTenantContext: (state, action: PayloadAction<AuthTenant | null>) => {
      state.tenant = action.payload;
    },
    setUserProfile: (state, action: PayloadAction<AuthUser | null>) => {
      state.user = action.payload;
    },
    clearAuth: (state) => {
      state.user = null;
      state.tenant = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.status = "anonymous";
      state.isAuthenticated = false;
      state.showAccountLocked = false;
    },
    setAccountLockedVisible: (state, action: PayloadAction<boolean>) => {
      state.showAccountLocked = action.payload;
    },
    markSessionExpired: (state) => {
      state.user = null;
      state.tenant = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.status = "anonymous";
      state.isAuthenticated = false;
      state.showAccountLocked = false;
      state.sessionExpiredNoticeId += 1;
    },
    clearSessionExpiredNotice: (state) => {
      state.sessionExpiredNoticeId = 0;
    },
  },
});

export const {
  clearAuth,
  clearSessionExpiredNotice,
  markSessionExpired,
  setAccessToken,
  setRefreshToken,
  setAccountLockedVisible,
  setAuthSession,
  setAuthStatus,
  setTenantContext,
  setUserProfile,
} = authSlice.actions;

export default authSlice.reducer;
